import { useCallback, useLayoutEffect, useState, type RefObject } from "react";
import { measureSafeAreaTopPx } from "./viewportFill";

function measurePhysicalTop(el: Element): number {
  const rect = el.getBoundingClientRect();
  const vvOffset = window.visualViewport?.offsetTop ?? 0;
  return rect.top + vvOffset;
}

/**
 * 顶部非安全区补偿：元素距物理屏顶 < safe-area-inset-top 时返回需补的 padding（px），否则 0。
 * 用于避免在微信等已下移 WebView 的场景下盲目叠加 env(safe-area-inset-top)。
 */
export function calcSafeAreaTopPadPx(el: Element | null): number {
  if (!el) {
    return 0;
  }
  const safeTop = measureSafeAreaTopPx();
  if (safeTop <= 0) {
    return 0;
  }
  const top = measurePhysicalTop(el);
  if (top >= safeTop) {
    return 0;
  }
  return Math.ceil(safeTop - top);
}

/** 在 mount / resize 时测量 ref 元素，返回需额外叠加的 padding-top（px） */
export function useSafeAreaTopPad(ref: RefObject<HTMLElement | null>): number {
  const [pad, setPad] = useState(0);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setPad(0);
      return;
    }
    const scrollRoot =
      el.closest(".lf-h5-space-main") ??
      el.closest(".h5-sub-page__body") ??
      el.closest(".lf-h5-page-scroll") ??
      el.parentElement;
    if (scrollRoot && scrollRoot.scrollTop > 1) {
      return;
    }
    setPad(calcSafeAreaTopPadPx(el));
  }, [ref]);

  useLayoutEffect(() => {
    sync();
    const raf = requestAnimationFrame(sync);

    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    window.visualViewport?.addEventListener("resize", sync);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.visualViewport?.removeEventListener("resize", sync);
    };
  }, [sync]);

  return pad;
}
